import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SaudiRiyalSymbol from '../../../components/base/icons/SaudiRiyalSymbol';
import { Reveal } from './_shared';

/**
 * The home page's equal journey cards (owner, 2026-10-07), modelled on
 * wasalt.sa's promo cards: centred title in ink, one short muted line, an
 * outline pill CTA, and a small product mockup at the foot of the card that the
 * card's own edge crops. Measured from wasalt: 16px radius, 40px top padding,
 * 24px/600 title, 16px muted subtitle, 52px fully-rounded pill with a 1px
 * hairline. The mockups are built in code from the site's own pieces (the city
 * photos already on the page, the riyal symbol), so they cost no extra download,
 * follow dark mode, and switch language with the site.
 *
 * Shared by ActionCards (add · request · invest) and HomeNextSteps (account ·
 * professionals · plans), so the two rows stay one pattern. The cards share row
 * tracks (CSS subgrid) from md up, so titles, subtitles, buttons and mockups line
 * up across a row even when one line is longer.
 */

export interface JourneyCard {
  key: string;
  to: string;
  title: string;
  body: string;
  cta: string;
  /** Decorative: the card text carries the meaning. */
  visual: ReactNode;
}

export function JourneyCards({ cards }: { cards: readonly JourneyCard[] }) {
  const { i18n } = useTranslation();
  const arrow = i18n.dir() === 'rtl' ? 'ri-arrow-left-line' : 'ri-arrow-right-line';
  return (
    // gap-y-0 from md: subgrid rows inherit the parent's row gap, and the
    // spacing inside a card is set by its children's margins instead.
    <div className="grid grid-cols-1 gap-5 md:grid-cols-3 md:gap-y-0">
      {cards.map((card) => (
        <Link
          key={card.key}
          to={card.to}
          className="group relative flex flex-col overflow-hidden rounded-[16px] border border-[var(--pv-border-soft)] bg-[var(--pv-color-surface)] pt-10 text-center shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-shadow duration-300 hover:shadow-[0_20px_44px_-28px_rgba(16,24,40,0.35)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)] focus-visible:ring-offset-2 md:row-span-4 md:grid md:grid-rows-subgrid"
        >
          <h3 className="text-balance px-6 font-display text-[1.4rem] font-semibold leading-snug text-[var(--pv-color-text)]">
            {card.title}
          </h3>
          <p className="mx-auto mt-3 max-w-[21rem] px-6 text-[15px] leading-relaxed text-[var(--pv-color-text-muted)]">
            {card.body}
          </p>
          {/* An outline pill, not a button inside a link: the whole card is
              the link, this is its visible affordance. */}
          <span className="mx-auto mt-6 inline-flex h-[52px] items-center gap-2 self-start justify-self-center rounded-full border border-[var(--pv-border-strong)] bg-[var(--pv-color-surface)] px-6 text-[15px] font-semibold text-[var(--pv-color-text)] transition-colors duration-200 group-hover:border-[var(--pv-color-primary)] group-hover:text-[var(--pv-color-primary)]">
            {card.cta}
            <i className={`${arrow} text-base transition-transform duration-200 group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5`} aria-hidden="true" />
          </span>
          {/* The mockup stage. The card's own bottom edge crops the screen. */}
          <div aria-hidden="true" className="relative mt-8 h-[230px] overflow-hidden">
            <div className="absolute inset-0 transition-transform duration-500 ease-out group-hover:-translate-y-1.5 motion-reduce:transition-none">
              {card.visual}
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mockup pieces. Decorative (aria-hidden at the stage). Rows rise in once when
// scrolled into view (Reveal), then stay.
// ---------------------------------------------------------------------------

/** The top of a phone; the card edge hides the rest. */
export function PhoneTop({ children }: { children: ReactNode }) {
  return (
    <div className="absolute bottom-0 left-1/2 h-[214px] w-[204px] -translate-x-1/2 translate-y-[18px] rounded-t-[30px] border-[6px] border-b-0 border-[#1D2B29] bg-[var(--pv-color-surface-soft)] dark:border-[#2E4A44]">
      <span className="absolute left-1/2 top-2 h-[16px] w-[62px] -translate-x-1/2 rounded-full bg-[#1D2B29] dark:bg-[#2E4A44]" />
      <div className="px-3.5 pt-9 text-start">{children}</div>
    </div>
  );
}

export function StatusChip({ tone, children }: { tone: 'live' | 'review'; children: ReactNode }) {
  // THEME.md §2.4c: teal for a settled state; cyan is the register's colour
  // for "in progress", which is what a listing under review is.
  const cls =
    tone === 'live'
      ? 'bg-[var(--pv-color-surface-sunken)] text-[var(--pv-color-primary)]'
      : 'bg-[#E2F6FD] text-[#0B5E78] dark:bg-[#0B3442] dark:text-[#8FD8EF]';
  return <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${cls}`}>{children}</span>;
}

/** One listing as a row: photo, title, price, and a mark at the end. */
export function ListingRow({ img, title, price, per, chip, delay }: { img: string; title: string; price: string; per?: string; chip: ReactNode; delay: number }) {
  return (
    <Reveal delay={delay}>
      <div className="flex items-center gap-2.5 rounded-xl bg-[var(--pv-color-surface-strong)] p-2 text-start shadow-[0_8px_20px_-10px_rgba(16,24,40,0.28)] ring-1 ring-[var(--pv-border-subtle)]">
        <img src={img} alt="" className="h-11 w-11 shrink-0 rounded-lg object-cover" loading="lazy" decoding="async" />
        {/* Title gets the full width; price and the mark share the second line. */}
        <div className="min-w-0 flex-1">
          <p className="truncate text-[12.5px] font-semibold text-[var(--pv-color-text)]">{title}</p>
          <div className="mt-1 flex items-center justify-between gap-2">
            <p className="flex items-center gap-1 whitespace-nowrap text-[11.5px] tabular-nums text-[var(--pv-color-text-muted)]">
              {/* explicit equal w/h, as everywhere else on the site: an inline
                  SVG with w-auto has no width limit and renders huge */}
              <SaudiRiyalSymbol title="" className="h-[11px] w-[11px] shrink-0" />
              {price}
              {per && <span>{per}</span>}
            </p>
            {chip}
          </div>
        </div>
      </div>
    </Reveal>
  );
}
